import { Box, Button, TextField, Typography, Paper, Alert, CircularProgress, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { checkStatus } from 'store/application/application-status';
import { Badge, Person, Search } from '@mui/icons-material';
import LanguageToggle from 'components/LanguageToggle';
import { useLocation } from 'react-router-dom';

export default function LoginForm() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loading, error } = useSelector((state) => state.application.applicationStatus || {});
    const location = useLocation()
    const defaultApplicationId = location.pathname.includes('/application-status/') 
        ? location.pathname.split('/application-status/').pop() || '' 
        : '';
    
    const { control, handleSubmit } = useForm({
        defaultValues: {
            applicationId: defaultApplicationId,
            lastName: ''
        }
    });

    const onSubmit = (data) => {
        dispatch(checkStatus({
            applicationId: data.applicationId,
            lastName: data.lastName
        }));
    };

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            sx={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)', p: 2 }}
            p={2}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0px 10px 40px -10px rgba(0,0,0,0.1)'
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        pt: 5,
                        pb: 4,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Box
                        component="img"
                        src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28112613/iLearn-Logo-White.png"
                        alt="iLearn Logo"
                        sx={{
                            width: '200px',
                            height: 'auto',
                            mb: 2,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                        }}
                    />

                    <Typography variant="h5" component="h1" fontWeight="600" letterSpacing={0.5}>
                        {t('Application Status')}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {t('Check your child\'s application status')}
                    </Typography>
                </Box>

                <Box sx={{ p: 4, bgcolor: 'background.paper' }}>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {t(typeof error === 'string' ? error : 'Record not found or verify your inputs')}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>

                        <Controller
                            name="applicationId"
                            control={control}
                            rules={{ required: t('Application ID is required') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    label={t("Application ID")}
                                    variant="outlined"
                                    margin="normal"
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Badge color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            )}
                        />

                        <Controller
                            name="lastName"
                            control={control}
                            rules={{ required: t('Last Name is required') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    label={t("Student Last Name")}
                                    variant="outlined"
                                    margin="normal"
                                    error={!!error}
                                    helperText={error ? error.message : null}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 4, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            )}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            size="large"
                            startIcon={!loading && <Search />}
                            sx={{
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                }
                            }}
                        >
                            {loading && <CircularProgress color="inherit" size="1.2rem" sx={{ mr: 1 }} />}
                            {loading ? t('Loading...') : t('Check Status')}
                        </Button>
                    </Box>
                    <Paper sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
                        <LanguageToggle />
                    </Paper>
                </Box>
            </Paper>
        </Box>
    );
}